/**
 * Hand-tracking gestures, classified from the per-hand summary the pose broadcast already
 * uses (finger curl, pinch, wrist position) plus the thumb and index tips. Pure and timed by
 * the caller, so it runs under Vitest:
 *
 *   thumbs up      thumb out, four fingers curled, thumb tip above the wrist   -> reaction
 *   flat palm up   every finger straight, hand above the eyes, held 2 s        -> raise hand
 *   pinch and pull pinch on a panel then pull toward the head within 0.6 s     -> grab panel
 *   double pinch   two pinch starts within 450 ms on empty space               -> wrist menu
 *   frame          both hands in an L (thumb and index out), level, held 0.8 s -> screenshot
 */
export interface GestureHand {
  id: string;
  /** Per-finger curl 0..1: thumb, index, middle, ring, pinky. */
  curl: [number, number, number, number, number];
  pinch: number;
  wrist: { x: number; y: number; z: number };
  thumbTip?: { x: number; y: number; z: number };
  /** True when the hand's pointer ray currently hits something interactive. */
  hitting: boolean;
  /** True when the ray hits a grabbable panel. */
  onPanel: boolean;
}

export interface GestureHead {
  position: { x: number; y: number; z: number };
}

export type Gesture =
  | { kind: 'thumbsUp'; hand: string }
  | { kind: 'handRaised'; hand: string }
  | { kind: 'pinchPull'; hand: string }
  | { kind: 'doublePinch'; hand: string }
  | { kind: 'frame' };

export const GESTURE = {
  raiseHoldMs: 2000,
  raiseCooldownMs: 1500,
  reactionCooldownMs: 1200,
  doublePinchMs: 450,
  pullWindowMs: 600,
  pullDistance: 0.08,
  frameHoldMs: 800,
  frameCooldownMs: 3000,
  pinchOn: 0.8,
  pinchOff: 0.35,
};

interface HandState {
  pinching: boolean;
  pinchStartedAt: number;
  pinchStart: { x: number; y: number; z: number; onPanel: boolean; headDistance: number };
  lastPinchStartAt: number;
  raiseSince: number | undefined;
  lastRaise: number;
  lastThumbs: number;
  thumbsHeldSince: number | undefined;
  pulled: boolean;
}

const dist = (a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

export function isThumbsUp(h: GestureHand): boolean {
  if (h.curl[0] > 0.35) return false;
  if (h.curl.slice(1).some((c) => c < 0.6)) return false;
  return !!h.thumbTip && h.thumbTip.y - h.wrist.y > 0.05;
}

export function isFlatPalm(h: GestureHand): boolean {
  return h.curl.every((c) => c < 0.3) && h.pinch < 0.3;
}

export function isLShape(h: GestureHand): boolean {
  return (
    h.curl[0] < 0.4 && h.curl[1] < 0.3 && h.curl[2] > 0.6 && h.curl[3] > 0.6 && h.curl[4] > 0.6
  );
}

export class GestureDetector {
  private readonly hands = new Map<string, HandState>();
  private frameSince: number | undefined;
  private lastFrame = -Infinity;

  private state(id: string): HandState {
    let s = this.hands.get(id);
    if (!s) {
      s = {
        pinching: false,
        pinchStartedAt: -Infinity,
        pinchStart: { x: 0, y: 0, z: 0, onPanel: false, headDistance: 0 },
        lastPinchStartAt: -Infinity,
        raiseSince: undefined,
        lastRaise: -Infinity,
        lastThumbs: -Infinity,
        thumbsHeldSince: undefined,
        pulled: false,
      };
      this.hands.set(id, s);
    }
    return s;
  }

  /** Feeds one frame of tracked hands; returns the gestures that completed this frame. */
  update(hands: GestureHand[], head: GestureHead, now: number): Gesture[] {
    const out: Gesture[] = [];
    for (const h of hands) {
      const s = this.state(h.id);
      // Thumbs up: held for a few frames (150 ms) so a passing pose does not fire.
      if (isThumbsUp(h)) {
        s.thumbsHeldSince ??= now;
        if (now - s.thumbsHeldSince >= 150 && now - s.lastThumbs >= GESTURE.reactionCooldownMs) {
          s.lastThumbs = now;
          out.push({ kind: 'thumbsUp', hand: h.id });
        }
      } else s.thumbsHeldSince = undefined;
      // Flat palm above the eyes for two seconds.
      if (isFlatPalm(h) && h.wrist.y > head.position.y - 0.05) {
        s.raiseSince ??= now;
        if (
          now - s.raiseSince >= GESTURE.raiseHoldMs &&
          now - s.lastRaise >= GESTURE.raiseCooldownMs
        ) {
          s.lastRaise = now;
          s.raiseSince = undefined;
          out.push({ kind: 'handRaised', hand: h.id });
        }
      } else s.raiseSince = undefined;
      // Pinch edges with hysteresis.
      if (!s.pinching && h.pinch >= GESTURE.pinchOn) {
        s.pinching = true;
        s.pulled = false;
        s.pinchStartedAt = now;
        s.pinchStart = {
          x: h.wrist.x,
          y: h.wrist.y,
          z: h.wrist.z,
          onPanel: h.onPanel,
          headDistance: dist(h.wrist, head.position),
        };
        if (!h.hitting && now - s.lastPinchStartAt <= GESTURE.doublePinchMs) {
          out.push({ kind: 'doublePinch', hand: h.id });
          s.lastPinchStartAt = -Infinity;
        } else s.lastPinchStartAt = h.hitting ? -Infinity : now;
      } else if (s.pinching && h.pinch <= GESTURE.pinchOff) {
        s.pinching = false;
      } else if (s.pinching && !s.pulled && s.pinchStart.onPanel) {
        // Pull: the hand comes toward the head by the threshold within the window.
        const closer = s.pinchStart.headDistance - dist(h.wrist, head.position);
        if (now - s.pinchStartedAt <= GESTURE.pullWindowMs && closer >= GESTURE.pullDistance) {
          s.pulled = true;
          out.push({ kind: 'pinchPull', hand: h.id });
        }
      }
    }
    // Two-hand frame.
    const ls = hands.filter(isLShape);
    const framing =
      ls.length === 2 &&
      Math.abs(ls[0]!.wrist.y - ls[1]!.wrist.y) < 0.15 &&
      dist(ls[0]!.wrist, ls[1]!.wrist) > 0.15 &&
      dist(ls[0]!.wrist, ls[1]!.wrist) < 0.7;
    if (framing) {
      this.frameSince ??= now;
      if (
        now - this.frameSince >= GESTURE.frameHoldMs &&
        now - this.lastFrame >= GESTURE.frameCooldownMs
      ) {
        this.lastFrame = now;
        this.frameSince = undefined;
        out.push({ kind: 'frame' });
      }
    } else this.frameSince = undefined;
    return out;
  }

  /** Whether a hand is mid pinch-pull (the panel stays held until the pinch opens). */
  holding(handId: string): boolean {
    const s = this.hands.get(handId);
    return !!s && s.pinching && s.pulled;
  }

  forget(handId: string): void {
    this.hands.delete(handId);
  }
}
